import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { canCancelOrder, canRestoreOrder } from "@msuite/utils/stock";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { OrderListItem } from "@msuite/utils/types";
import { client } from "@/hono";

export function RowActions({ cell }: CellContext<OrderListItem, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Hooks */
	function handleOnChangeOrderState(operation: "cancel" | "restore") {
		toast.promise(
			async () => {
				const response = await client.stock.orders[":orderId"][operation].$patch({
					param: { orderId: cell.row.original.id },
				});
				await responseHandler(response);
				await refetch?.();
			},
			{
				loading: t(operation === "cancel" ? "stock.cancelling_order" : "stock.restoring_order"),
				success: t(
					operation === "cancel"
						? "stock.cancelling_order_successful"
						: "stock.restoring_order_successful",
				),
				error: t(
					operation === "cancel" ? "stock.cancelling_order_failed" : "stock.restoring_order_failed",
				),
			},
		);
	}

	/** Computed */
	const { state } = cell.row.original;

	/** Render */
	return (
		<RowActionsWrapper to={`/orders/${cell.row.original.id}`}>
			<Menu
				items={createStandardMenuItems({
					items: [
						{
							icon: icon("ArchiveRestore"),
							label: t("stock.restore_order"),
							isDisabled: !canRestoreOrder(state),
							onClick: () => handleOnChangeOrderState("restore"),
						},
					],
					onDelete: () => handleOnChangeOrderState("cancel"),
					onDeleteOptions: {
						icon: icon("CircleX"),
						label: t("stock.cancel_order"),
						// Past `open`/`pending` a picker has taken stock off shelves, so the order can no
						// longer be withdrawn cleanly — the handler refuses it with 409 either way.
						isDisabled: !canCancelOrder(state),
					},
				})}
			>
				<Menu.Button />
			</Menu>
		</RowActionsWrapper>
	);
}
